import React, { useRef, useEffect } from "react";
import ReactDOMServer from "react-dom/server";
import Supercluster from "supercluster";
import { MapContainer, MapSpinner, MapDiv } from "./Map/MapComponents";
import dayjs from "dayjs";
import { getItem } from "../../mdtApi/getData";
import { FilterType } from "../../types/filters";
import {
  aggregateManualEvents,
  getEvents,
  getManualEvents,
} from "../../util/data/itemsDataFunctions";
import { colors } from "../../styles/colors";
import { useState } from "react";
import ManualEventMarker from "./Map/ManualEventMarker";
import ManualEventPopup from "./Map/ManualEventPopup";

const ItemsMap = ({
  interactive,
  loading,
  geojson,
  cluster,
  filters,
  setFilterPolygon,
  draw,
}: {
  interactive: boolean;
  cluster: boolean;
  loading: boolean;
  draw: boolean;
  geojson: GeoJSON.FeatureCollection;
  setFilterPolygon?: (b: L.Polygon) => void;
  filters: FilterType;
}) => {
  const L = require("leaflet");
  require("leaflet-draw");

  const [isBrowser, setIsBrowser] = useState<boolean>(false);

  const BASEMAP = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
  const ATTRIBUTION =
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
  const map = useRef<L.Map>(undefined);
  const mapDivRef = useRef<any>(undefined);
  const drawnItems = useRef<L.FeatureGroup>(undefined);

  useEffect(() => {
    setIsBrowser(true);
  }, []);

  useEffect(() => {
    createMap();
  }, [isBrowser, geojson]);

  const createMap = async () => {
    const createClusterCustomIcon = (feature, latlng) => {
      if (!feature.properties.cluster) {
        let marker = L.circleMarker(latlng, { color: "#64748a" });
        marker.bindPopup("Loading...");
        marker.on("click", async (e) => {
          let popup = e.target.getPopup();
          let itemData = await getItem(feature.properties.id);

          try {
            let item = itemData;
            popup.setContent(
              `${item.quantity > 1 ? item.quantity : ""} ${
                item.quantity <= 1 &&
                item.itemname.charAt(item.itemname.length - 1) === "s"
                  ? item.itemname.slice(0, item.itemname.length - 1)
                  : item.itemname
              } <br/> Collected on ${dayjs(item.timestamp, "YYYYMMDD").format(
                "YYYY-MM-DD"
              )}`
            );
            popup.update();
          } catch {}
        });
        return marker;
      }
      let iconSettings = {
        mapIconUrl:
          '<svg version="1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 149 178"><path fill="{mapIconColor}" stroke="{mapIconStroke}" stroke-width="6" stroke-miterlimit="10" d="M126 23l-6-6A69 69 0 0 0 74 1a69 69 0 0 0-51 22A70 70 0 0 0 1 74c0 21 7 38 22 52l43 47c6 6 11 6 16 0l48-51c12-13 18-29 18-48 0-20-8-37-22-51z"/><circle fill="{mapIconColorInnerCircle}" cx="74" cy="75" r="61"/><circle fill="{mapIconColorInnerCircle}" cx="74" cy="75" r="{pinInnerCircleRadius}"/>{mapText}</svg>',
        mapIconColor: "#64748a",
        mapIconStroke: "#64748aB3",
        mapIconColorInnerCircle: "#edbd7f",
        mapText: `<text style="font-size:40pt" text-anchor="middle" x="50%" y="50%">${feature.properties.point_count_abbreviated}</text>`,
        pinInnerCircleRadius: 48,
      };
      let degradedIconSettings = {
        mapIconUrl:
          '<svg version="1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 149 178"><path fill="{mapIconColor}" stroke="{mapIconStroke}" stroke-width="6" stroke-miterlimit="10" d="M126 23l-6-6A69 69 0 0 0 74 1a69 69 0 0 0-51 22A70 70 0 0 0 1 74c0 21 7 38 22 52l43 47c6 6 11 6 16 0l48-51c12-13 18-29 18-48 0-20-8-37-22-51z"/><circle fill="{mapIconColorInnerCircle}" cx="74" cy="75" r="61"/><circle fill="{mapIconColorInnerCircle}" cx="74" cy="75" r="{pinInnerCircleRadius}"/>{mapText}</svg>',
        mapIconColor: "#64748a",
        mapIconStroke: "#64748aB3",
        mapIconColorInnerCircle: "#edbd7f",
        mapText: `<text style="font-size:40pt" text-anchor="middle" x="50%" y="50%">${feature.properties.point_count_abbreviated}</text>`,
        pinInnerCircleRadius: 48,
      };

      let icon = L.divIcon({
        className: "leaflet-data-marker",
        html: L.Util.template(iconSettings.mapIconUrl, iconSettings),
        iconAnchor: [18, 42],
        iconSize: [36, 42],
        popupAnchor: [0, -30],
      });
      let marker = L.marker(latlng, { icon: icon });
      return marker;
    };

    const centerMapOnCluster = (cluster, center) => {
      map.current.flyTo(center, index.getClusterExpansionZoom(cluster));
    };

    const loadDataAndCreateIndex = async (geojsonToLoad) => {
      return new Supercluster({
        radius: 40,
        extent: 256,
        maxZoom: 17,
      }).load(geojsonToLoad.features);
    };

    const update = (markers) => {
      const bounds = map.current.getBounds();

      let clusters;
      try {
        clusters = index.getClusters(
          [
            bounds.getWest(),
            bounds.getSouth(),
            bounds.getEast(),
            bounds.getNorth(),
          ],
          map.current.getZoom()
        );
      } catch {
        console.error(`FAILED`);
        return null;
      }

      markers.clearLayers();
      markers.addData(clusters);
      map.current.dragging.enable();
      return clusters;
    };

    // init map and grab data
    if (!map.current) {
      if (!mapDivRef.current) {
        return;
      }
      let l_map = L.map("map", {
        maxZoom: interactive ? 22 : 15,
        dragging: true,
        zoomControl: interactive,
      }).setView([30, 0], 3);
      L.tileLayer(BASEMAP, {
        maxNativeZoom: 19,
        maxZoom: 22,
        attribution: ATTRIBUTION,
        noWrap: true,
      }).addTo(l_map);
      // enable draw polygon
      if (draw) {
        if (!drawnItems.current) {
          let drawnItemsLayer = new L.FeatureGroup();
          drawnItems.current = drawnItemsLayer;
        }
        l_map.addLayer(drawnItems.current);

        L.EditToolbar.Delete.include({
          removeAllLayers: false,
        });

        const drawOptions: L.Control.DrawConstructorOptions = {
          position: "topright",
          draw: {
            polyline: false,
            rectangle: false,
            circle: false,
            marker: false,
            circlemarker: false,
            polygon: {
              allowIntersection: false, // Restricts shapes to simple polygons
              drawError: {
                color: colors.orange, // Color the shape will turn when intersects
                message:
                  "<strong>Error<strong> you can't draw complex polygons!", // Message that will show when intersect
              },
              shapeOptions: {
                color: colors.orange,
              },
            },
          },
          edit: {
            featureGroup: drawnItems.current,
            edit: false,
          },
        };
        let drawControl = new L.Control.Draw(drawOptions);

        l_map.addControl(drawControl);
      }
      map.current = l_map;
    } else {
      map.current.eachLayer((layer) => {
        //@ts-ignore
        if (!layer._url || layer._url !== BASEMAP) {
          map.current.removeLayer(layer);
        }
      });
      if (interactive) map.current.zoomControl.setPosition("bottomright");
    }
    if (cluster) {
      if (!geojson) {
        return;
      }

      let filteredGeojson = geojson;
      if (filters && filters.list !== "all" && filters.date_type !== "all") {
        filteredGeojson = getEvents(geojson);
        if (!filteredGeojson) {
          return;
        }
      }

      var index = await loadDataAndCreateIndex(filteredGeojson);

      const markers = L.geoJSON(null, {
        pointToLayer: createClusterCustomIcon,
      }).addTo(map.current);

      update(markers);

      map.current.on("moveend", () => {
        update(markers);
      });

      // add drawn polygons back to map
      if (draw) {
        map.current.addLayer(drawnItems.current);
      }

      if (draw) {
        //@ts-ignore
        map.current.on("draw:created", (e: L.DrawEvents.Created) => {
          const layer = e.layer;
          let count = 0;
          drawnItems.current.eachLayer(() => count++);
          if (count > 1) {
            drawnItems.current.eachLayer((l) =>
              drawnItems.current.removeLayer(l)
            );
          } else {
            drawnItems.current.addLayer(layer);
          }

          // Filter data inside bounds
          if (e.layerType === "polygon") {
            setFilterPolygon(e.layer as L.Polygon);
          }
        });

        map.current.on("draw:deleted", () => {
          setFilterPolygon(undefined);
        });
      }

      markers.on("click", (e) => {
        if (e.layer.feature.properties.cluster_id) {
          map.current.dragging.disable();
          //@ts-ignore
          centerMapOnCluster(e.layer.feature.properties.cluster_id, e.latlng);
        }
      });
    } else {
      const markers = L.geoJSON(geojson);

      markers.addTo(map.current);
      if (geojson.features.length > 0) {
        map.current.fitBounds(markers.getBounds());
      }
    }
    if (!interactive) {
      map.current.dragging.disable();
      map.current.touchZoom.disable();
      map.current.doubleClickZoom.disable();
      map.current.scrollWheelZoom.disable();
      map.current.boxZoom.disable();
      map.current.keyboard.disable();
      if (map.current.tap) map.current.tap.disable();
      document.getElementById("map").style.cursor = "default";
    }
  };

  // Handle manual events
  useEffect(() => {
    const createManualEventMarker = (feature, latlng) => {
      //working here
      const fontAwesomeIcon = L.divIcon({
        html: ReactDOMServer.renderToString(<ManualEventMarker />),
        iconAnchor: [10, 40],
        popupAnchor: [20, -40],
        className: "markerDiv",
      });
      let marker = L.marker(latlng, { icon: fontAwesomeIcon });
      marker.bindPopup(
        ReactDOMServer.renderToString(<ManualEventPopup feature={feature} />)
      );
      return marker;
    };

    map.current &&
      map.current.eachLayer((layer) => {
        //@ts-ignore
        if (layer.myTag && layer.myTag === "manualLayer") {
          map.current.removeLayer(layer);
        }
      });

    if (
      geojson &&
      geojson.features.length > 0 &&
      filters.showManual &&
      map.current
    ) {
      let manualData = getManualEvents(geojson);
      manualData.features = aggregateManualEvents(manualData.features);
      filters.showManual &&
        L.geoJSON(manualData, {
          pointToLayer: createManualEventMarker,
          //@ts-ignore
          onEachFeature: (f, l) => (l.myTag = "manualLayer"),
        }).addTo(map.current);
    }
  }, [geojson, filters.showManual]);

  if (!isBrowser) {
    return null;
  }

  return (
    <MapContainer>
      {loading ? <MapSpinner /> : null}
      <MapDiv ref={mapDivRef} id="map" />
    </MapContainer>
  );
};

export default ItemsMap;
